import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { mainApiUrl, storeId as storeIdConstant } from '@constants'
import { base62Encode } from '@src/utils'
import { prepareHeaders, transformResponse } from '@src/reduxjs'

const albumsApi = createApi({
  reducerPath: 'albumsApi',
  baseQuery: fetchBaseQuery({ baseUrl: mainApiUrl, prepareHeaders }),
  tagTypes: ['Albums', 'LabelsForAlbums'],
  endpoints: (builder) => ({
    getLabelsForAlbum: builder.query({
      query: (album) => {
        return {
          url: `albums/${album.id}/labels`,
          method: 'GET',
        }
      },
      providesTags: (results, error, { id }) => {
        return [{ type: 'LabelsForAlbums', id }]
      },
      transformResponse,
    }),
    getJobsForAlbum: builder.query({
      query: (album) => {
        return {
          url: `albums/${album.id}/jobs`,
          method: 'GET',
        }
      },
      transformResponse,
    }),
    getPhotographersForAlbum: builder.query({
      query: (album) => `albums/${album.id}/photographers`,
      transformResponse,
    }),
    getStoreAlbums: builder.query({
      query: ({ storeId, page, perPage, showHidden, dates, timezone } = {}) => {
        const params = {}

        if (page) {
          params.page = page
        }

        if (perPage) {
          params.perPage = perPage
        }

        if (showHidden) {
          params.show_hidden = showHidden
        }

        if (dates) {
          params.dates = dates
        }

        if (timezone) {
          params.timezone = base62Encode(timezone)
        }

        return { url: `stores/${storeId || storeIdConstant}/albums`, params }
      },
      providesTags: (data) => {
        return [ ...(data || []).map((i) => ({ type: 'Albums', id: i.id })), { type: 'Albums', id: 'LIST' } ]
      },
      transformResponse,
    }),
    createAlbum: builder.mutation({
      query: (data) => {
        return {
          url: `albums`,
          method: 'POST',
          body: data,
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    updateAlbum: builder.mutation({
      query: (data) => {
        return {
          url: `albums/${data.id}`,
          method: 'PATCH',
          body: data,
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    deleteAlbum: builder.mutation({
      query: ({ id }) => {
        return {
          url: `albums/${id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'Albums', id: data.id }]
      },
    }),
    createLabelForAlbum: builder.mutation({
      query: ({ album, label }) => {
        return {
          url: `albums/${album.id}/labels`,
          method: 'POST',
          body: label,
        }
      },
      invalidatesTags: (results, error, { album, label }) => {
        return [{ type: 'LabelsForAlbums', id: album.id }]
      },
    }),
    deleteLabelForAlbum: builder.mutation({
      query: ({ album, label }) => {
        return {
          url: `albums/${album.id}/labels/${label.id}`,
          method: 'DELETE',
        }
      },
      invalidatesTags: (results, error, data) => {
        return [{ type: 'LabelsForAlbums', id: data.album.id }]
      },
    }),
  }),
})

export const {
  useGetLabelsForAlbumQuery,
  useGetJobsForAlbumQuery,
  useGetPhotographersForAlbumQuery,
  useCreateAlbumMutation,
  useUpdateAlbumMutation,
  useCreateLabelForAlbumMutation,
  useDeleteLabelForAlbumMutation,
  useDeleteAlbumMutation,
  useGetStoreAlbumsQuery,
} = albumsApi

export default albumsApi
